import React,{useState} from 'react';
import {useAuth} from '../context/AuthContext';
import {useTheme} from '../context/ThemeContext';
import {authAPI} from '../utils/api';
import {
  X,User,Lock,Eye,EyeOff,Sun,Moon,Save,
  Phone,Shield,Building2,CheckCircle,AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

const lbl={display:'block',fontSize:11,fontWeight:600,color:'var(--tm)',marginBottom:5,letterSpacing:.3};
const inp={width:'100%',padding:'9px 12px',borderRadius:8,border:'1.5px solid var(--border)',
  background:'var(--s2)',color:'var(--text)',fontSize:13,outline:'none',
  fontFamily:'inherit',boxSizing:'border-box',transition:'border-color .15s'};

function pwStrength(pw){
  if(!pw)return null;
  let s=0;
  if(pw.length>=8)s++;if(pw.length>=12)s++;
  if(/[A-Z]/.test(pw))s++;if(/[0-9]/.test(pw))s++;if(/[^A-Za-z0-9]/.test(pw))s++;
  if(s<=1)return{label:'Weak',color:'var(--danger)',pct:20};
  if(s<=3)return{label:'Fair',color:'var(--warning)',pct:60};
  return{label:'Strong',color:'var(--success)',pct:100};
}

export default function UserProfileModal({onClose}){
  const{user}=useAuth();
  const{isDark,toggle:toggleTheme}=useTheme();

  const[firstName,setFirstName]=useState(user?.first_name||'');
  const[lastName,setLastName]=useState(user?.last_name||'');
  const[phone,setPhone]=useState(user?.phone||'');
  const[savingProfile,setSavingProfile]=useState(false);

  const[currentPw,setCurrentPw]=useState('');
  const[newPw,setNewPw]=useState('');
  const[confirmPw,setConfirmPw]=useState('');
  const[showCurrent,setShowCurrent]=useState(false);
  const[showNew,setShowNew]=useState(false);
  const[showConfirm,setShowConfirm]=useState(false);
  const[savingPw,setSavingPw]=useState(false);

  const strength=pwStrength(newPw);
  const pwMatch=newPw&&confirmPw&&newPw===confirmPw;
  const pwMismatch=!!confirmPw&&newPw!==confirmPw;

  const saveProfile=async()=>{
    if(!firstName.trim()){toast.error('First name is required');return;}
    setSavingProfile(true);
    try{
      await authAPI.updateProfile({first_name:firstName,last_name:lastName,phone});
      const stored=JSON.parse(localStorage.getItem('erp_user')||'{}');
      localStorage.setItem('erp_user',JSON.stringify({...stored,first_name:firstName,last_name:lastName,phone}));
      toast.success('Profile updated!');
    }catch(e){toast.error(e.response?.data?.error||'Failed to update');}
    finally{setSavingProfile(false);}
  };

  const savePassword=async()=>{
    if(!currentPw){toast.error('Enter current password');return;}
    if(newPw.length<8){toast.error('New password must be at least 8 characters');return;}
    if(newPw!==confirmPw){toast.error('Passwords do not match');return;}
    setSavingPw(true);
    try{
      await authAPI.updateProfile({current_password:currentPw,new_password:newPw});
      toast.success('Password changed!');
      setCurrentPw('');setNewPw('');setConfirmPw('');
    }catch(e){toast.error(e.response?.data?.error||'Failed to change password');}
    finally{setSavingPw(false);}
  };

  const initials=(user?.first_name?.[0]||'')+(user?.last_name?.[0]||'');

  const eyeBtn={
    position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',
    background:'none',border:'none',cursor:'pointer',color:'var(--td)',
    display:'flex',padding:4,
  };

  return(
    <div
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}
      style={{position:'fixed',inset:0,zIndex:2000,background:'rgba(0,0,0,.6)',
        backdropFilter:'blur(4px)',display:'flex',alignItems:'center',
        justifyContent:'center',padding:16}}>
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',
        borderRadius:16,width:'100%',maxWidth:480,maxHeight:'92vh',
        display:'flex',flexDirection:'column',overflow:'hidden'}}>

        {/* ── Header ── */}
        <div style={{padding:'16px 20px',borderBottom:'1px solid var(--border)',
          display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <div style={{width:40,height:40,borderRadius:10,background:'var(--pm)',
              display:'flex',alignItems:'center',justifyContent:'center',
              fontSize:15,fontWeight:800,color:'var(--primary)',flexShrink:0}}>
              {initials||'U'}
            </div>
            <div>
              <div style={{fontWeight:700,fontSize:14}}>{user?.first_name} {user?.last_name}</div>
              <div style={{fontSize:11,color:'var(--td)',display:'flex',alignItems:'center',gap:4}}>
                <Shield size={10}/>{user?.role?.name}
                {user?.branch&&<><Building2 size={10}/>{user.branch.name}</>}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',
            color:'var(--td)',display:'flex',borderRadius:8,padding:4}}>
            <X size={18}/>
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{flex:1,overflowY:'auto',padding:'0 20px 24px'}}>

          {/* Appearance */}
          <div style={{marginTop:18}}>
            <div style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',
              letterSpacing:.7,marginBottom:10,display:'flex',alignItems:'center',gap:6}}>
              <Sun size={12}/> Appearance / រូបរាង
            </div>
            <div
              onClick={toggleTheme}
              style={{display:'flex',alignItems:'center',justifyContent:'space-between',
                padding:'12px 14px',borderRadius:10,background:'var(--s2)',
                border:'1px solid var(--border)',cursor:'pointer'}}>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                {isDark?<Moon size={16} color="var(--primary)"/>:<Sun size={16} color="var(--warning)"/>}
                <div>
                  <div style={{fontSize:13,fontWeight:600}}>
                    {isDark?'Dark Mode / ម៉ូដងងឹត':'Light Mode / ម៉ូដភ្លឺ'}
                  </div>
                  <div style={{fontSize:11,color:'var(--td)'}}>
                    {isDark?'Switch to light':'Switch to dark'}
                  </div>
                </div>
              </div>
              <div style={{width:42,height:24,borderRadius:12,
                background:isDark?'var(--primary)':'var(--border)',
                display:'flex',alignItems:'center',padding:3,transition:'background .2s'}}>
                <div style={{width:18,height:18,borderRadius:9,background:'#fff',
                  transform:isDark?'translateX(18px)':'translateX(0)',transition:'transform .2s',
                  boxShadow:'0 1px 4px rgba(0,0,0,.25)'}}/>
              </div>
            </div>
          </div>

          <hr style={{border:'none',borderTop:'1px solid var(--border)',margin:'18px 0'}}/>

          {/* Profile info */}
          <div>
            <div style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',
              letterSpacing:.7,marginBottom:12,display:'flex',alignItems:'center',gap:6}}>
              <User size={12}/> Profile / ព័ត៌មានផ្ទាល់ខ្លួន
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
              <div>
                <label style={lbl}>First Name / នាម *</label>
                <input
                  value={firstName}
                  onChange={e=>setFirstName(e.target.value)}
                  placeholder="First name"
                  style={inp}
                  onFocus={e=>e.currentTarget.style.borderColor='var(--primary)'}
                  onBlur={e=>e.currentTarget.style.borderColor='var(--border)'}
                />
              </div>
              <div>
                <label style={lbl}>Last Name / គោត្ត</label>
                <input
                  value={lastName}
                  onChange={e=>setLastName(e.target.value)}
                  placeholder="Last name"
                  style={inp}
                  onFocus={e=>e.currentTarget.style.borderColor='var(--primary)'}
                  onBlur={e=>e.currentTarget.style.borderColor='var(--border)'}
                />
              </div>
            </div>

            <div style={{marginBottom:12}}>
              <label style={lbl}><Phone size={11} style={{marginRight:4}}/>Phone / ទូរស័ព្ទ</label>
              <input
                value={phone}
                onChange={e=>setPhone(e.target.value)}
                placeholder="+855 XX XXX XXX"
                style={inp}
                onFocus={e=>e.currentTarget.style.borderColor='var(--primary)'}
                onBlur={e=>e.currentTarget.style.borderColor='var(--border)'}
              />
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:14}}>
              {[['Email',user?.email],['Role',user?.role?.name]].map(([k,v])=>(
                <div key={k} style={{background:'var(--s2)',borderRadius:8,padding:'8px 12px',border:'1px solid var(--border)'}}>
                  <div style={{fontSize:10,color:'var(--td)',fontWeight:600,marginBottom:2}}>{k}</div>
                  <div style={{fontSize:12,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{v||'—'}</div>
                </div>
              ))}
            </div>

            <button onClick={saveProfile} disabled={savingProfile}
              className="btn btn-primary" style={{width:'100%',height:38,justifyContent:'center',gap:6}}>
              {savingProfile?'Saving…':<><Save size={14}/> Save Profile / រក្សាទុក</>}
            </button>
          </div>

          <hr style={{border:'none',borderTop:'1px solid var(--border)',margin:'18px 0'}}/>

          {/* Change Password */}
          <div>
            <div style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',
              letterSpacing:.7,marginBottom:12,display:'flex',alignItems:'center',gap:6}}>
              <Lock size={12}/> Change Password / ប្ដូរពាក្យសម្ងាត់
            </div>

            {/* Current password */}
            <div style={{marginBottom:12}}>
              <label style={lbl}>Current Password / ពាក្យសម្ងាត់បច្ចុប្បន្ន *</label>
              <div style={{position:'relative'}}>
                <input
                  type={showCurrent?'text':'password'}
                  value={currentPw}
                  onChange={e=>setCurrentPw(e.target.value)}
                  placeholder="••••••••"
                  style={{...inp,paddingRight:38}}
                  onFocus={e=>e.currentTarget.style.borderColor='var(--primary)'}
                  onBlur={e=>e.currentTarget.style.borderColor='var(--border)'}
                />
                <button style={eyeBtn} onClick={()=>setShowCurrent(v=>!v)} type="button" tabIndex={-1}>
                  {showCurrent?<EyeOff size={14}/>:<Eye size={14}/>}
                </button>
              </div>
            </div>

            {/* New password */}
            <div style={{marginBottom:4}}>
              <label style={lbl}>New Password / ពាក្យសម្ងាត់ថ្មី * (min 8 chars)</label>
              <div style={{position:'relative'}}>
                <input
                  type={showNew?'text':'password'}
                  value={newPw}
                  onChange={e=>setNewPw(e.target.value)}
                  placeholder="Min 8 characters"
                  style={{...inp,paddingRight:38}}
                  onFocus={e=>e.currentTarget.style.borderColor='var(--primary)'}
                  onBlur={e=>e.currentTarget.style.borderColor='var(--border)'}
                />
                <button style={eyeBtn} onClick={()=>setShowNew(v=>!v)} type="button" tabIndex={-1}>
                  {showNew?<EyeOff size={14}/>:<Eye size={14}/>}
                </button>
              </div>
            </div>

            {/* Strength bar */}
            {newPw&&strength&&(
              <div style={{marginBottom:12}}>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'var(--td)',marginBottom:4}}>
                  <span>Password Strength</span>
                  <span style={{color:strength.color,fontWeight:700}}>{strength.label}</span>
                </div>
                <div style={{height:4,background:'var(--border)',borderRadius:2,overflow:'hidden'}}>
                  <div style={{height:'100%',width:`${strength.pct}%`,background:strength.color,borderRadius:2,transition:'width .3s'}}/>
                </div>
              </div>
            )}

            {/* Confirm password */}
            <div style={{marginBottom:6}}>
              <label style={lbl}>Confirm Password / បញ្ជាក់ *</label>
              <div style={{position:'relative'}}>
                <input
                  type={showConfirm?'text':'password'}
                  value={confirmPw}
                  onChange={e=>setConfirmPw(e.target.value)}
                  placeholder="Re-enter new password"
                  style={{...inp,paddingRight:38,
                    borderColor:pwMismatch?'var(--danger)':pwMatch?'var(--success)':'var(--border)'}}
                  onFocus={e=>e.currentTarget.style.borderColor=pwMismatch?'var(--danger)':pwMatch?'var(--success)':'var(--primary)'}
                  onBlur={e=>e.currentTarget.style.borderColor=pwMismatch?'var(--danger)':pwMatch?'var(--success)':'var(--border)'}
                />
                <button style={eyeBtn} onClick={()=>setShowConfirm(v=>!v)} type="button" tabIndex={-1}>
                  {showConfirm?<EyeOff size={14}/>:<Eye size={14}/>}
                </button>
              </div>
            </div>

            {/* Match indicator */}
            {confirmPw&&(
              <div style={{display:'flex',alignItems:'center',gap:5,fontSize:11,marginBottom:12,
                color:pwMatch?'var(--success)':'var(--danger)'}}>
                {pwMatch?<CheckCircle size={12}/>:<AlertCircle size={12}/>}
                {pwMatch?'Passwords match ✓':'Passwords do not match'}
              </div>
            )}

            <button
              onClick={savePassword}
              disabled={savingPw||!currentPw||newPw.length<8||pwMismatch||!confirmPw}
              className="btn btn-primary"
              style={{width:'100%',height:38,justifyContent:'center',gap:6}}>
              {savingPw?'Changing…':<><Lock size={14}/> Change Password / ប្ដូរ</>}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
